import React from 'react';
import { Film, Swords, Palette, Music2, Car, Flame, Layers, Sparkles } from 'lucide-react';

interface CategoryNavProps {
  onOpenCollection: (type: string) => void;
}

const categories = [
  {
    id: 'Cinematic',
    label: 'Cinematic',
    icon: Film,
    description: 'Movies & TV Shows',
    accent: 'from-red-500/25 to-red-600/5',
    border: 'border-red-500/15 hover:border-red-500/40',
    iconColor: 'text-red-400',
    size: 'col-span-2 row-span-2', // Large hero tile
  },
  {
    id: 'Anime',
    label: 'Anime',
    icon: Swords,
    description: 'Manga & Anime Art',
    accent: 'from-pink-500/25 to-pink-600/5',
    border: 'border-pink-500/15 hover:border-pink-500/40',
    iconColor: 'text-pink-400',
    size: 'col-span-1 row-span-1',
  },
  {
    id: 'Sports',
    label: 'Sports',
    icon: Car,
    description: 'F1, Football & More',
    accent: 'from-blue-500/25 to-blue-600/5',
    border: 'border-blue-500/15 hover:border-blue-500/40',
    iconColor: 'text-blue-400',
    size: 'col-span-1 row-span-1',
  },
  {
    id: 'Abstract',
    label: 'Abstract',
    icon: Palette,
    description: 'Art & Architecture',
    accent: 'from-amber-500/25 to-amber-600/5',
    border: 'border-amber-500/15 hover:border-amber-500/40',
    iconColor: 'text-amber-400',
    size: 'col-span-1 row-span-1',
  },
  {
    id: 'Music',
    label: 'Music',
    icon: Music2,
    description: 'Bands & Artists',
    accent: 'from-amber-500/25 to-amber-600/5',
    border: 'border-amber-500/15 hover:border-amber-500/40',
    iconColor: 'text-amber-400',
    size: 'col-span-1 row-span-1',
  },
  {
    id: 'Stickers',
    label: 'Stickers & Skins',
    icon: Layers,
    description: 'Device Upgrades',
    accent: 'from-emerald-500/25 to-emerald-600/5',
    border: 'border-emerald-500/15 hover:border-emerald-500/40',
    iconColor: 'text-emerald-400',
    size: 'col-span-1 row-span-2', // Tall tile
  },
  {
    id: 'All',
    label: 'View All',
    icon: Sparkles,
    description: 'Full Collection',
    accent: 'from-white/10 to-white/5',
    border: 'border-white/10 hover:border-amber-500/40',
    iconColor: 'text-white',
    size: 'col-span-1 row-span-1',
  },
  {
    id: 'drops',
    label: 'Limited Drops',
    icon: Flame,
    description: 'Exclusive & Rare',
    accent: 'from-orange-500/25 to-orange-600/5',
    border: 'border-orange-500/15 hover:border-orange-500/40',
    iconColor: 'text-orange-400',
    size: 'col-span-1 row-span-1',
  },
];

const CategoryNav: React.FC<CategoryNavProps> = ({ onOpenCollection }) => {
  return (
    <section className="py-16 bg-neutral-900 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-amber-900/5 rounded-full blur-[100px] pointer-events-none translate-x-1/3 -translate-y-1/3"></div>

      <div className="container mx-auto px-6">
        {/* Section Header */}
        <div className="mb-10">
          <h2 className="text-xs font-bold tracking-[0.3em] text-amber-500 mb-3 uppercase">Quick Access</h2>
          <p className="text-2xl md:text-4xl font-black tracking-tighter uppercase">Browse by Category</p>
        </div>

        {/* Heap Grid Layout */}
        <div className="grid grid-cols-2 md:grid-cols-4 auto-rows-[100px] md:auto-rows-[120px] gap-3 md:gap-4">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => {
                if (cat.id === 'drops') {
                  // Scroll to drops section
                  document.getElementById('drops')?.scrollIntoView({ behavior: 'smooth' });
                } else {
                  onOpenCollection(cat.id);
                }
              }}
              className={`${cat.size} relative group rounded-2xl bg-gradient-to-br ${cat.accent} border ${cat.border} backdrop-blur-sm transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-black/20 overflow-hidden text-left p-5 md:p-6 flex flex-col justify-end cursor-pointer`}
            >
              {/* Background image placeholder — can add images later */}
              <div className="absolute inset-0 bg-neutral-900/30 group-hover:bg-neutral-900/10 transition-colors duration-300 pointer-events-none"></div>

              {/* Decorative large icon in background */}
              <div className={`absolute top-3 right-3 md:top-4 md:right-4 ${cat.iconColor} opacity-10 group-hover:opacity-20 transition-opacity duration-500`}>
                <cat.icon className="w-12 h-12 md:w-16 md:h-16" strokeWidth={1} />
              </div>

              {/* Content */}
              <div className="relative z-10">
                {/* Icon */}
                <div className={`${cat.iconColor} mb-2 group-hover:scale-110 transition-transform duration-300 inline-block`}>
                  <cat.icon className="w-5 h-5" strokeWidth={2} />
                </div>
                {/* Label */}
                <h3 className="text-white font-bold text-sm md:text-base tracking-wide uppercase leading-tight mb-0.5">
                  {cat.label}
                </h3>
                {/* Description */}
                <p className="text-neutral-400 text-[10px] md:text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300 translate-y-1 group-hover:translate-y-0">
                  {cat.description}
                </p>
              </div>

              {/* Hover shimmer effect */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none bg-gradient-to-tr from-transparent via-white/[0.03] to-transparent"></div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CategoryNav;
